import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Search, X, MapPin, Menu, Loader2, MessageSquare, Newspaper, Bookmark, Users, ArrowRight, ListPlus } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import { useTranslations } from '../locales/useI18n';
import { useAnalytics } from '../hooks/useAnalytics';
import EventFinderAPI from '../services/api';
import { VectorSearchResult, LocationSuggestion, EntitySuggestion } from '../types/api';
import { buildEntityEventsPath, buildEventPath } from '../utils/slug';
import { displayEntityType } from '../utils/entityTerminology';
import LocaleSwitcher from './LocaleSwitcher';
import config from '../config';
import HeaderActivityStatus from './HeaderActivityStatus';
import EntityAvatar from './EntityAvatar';

/** Default geographic search radius, matching the radius selector's default. */
const DEFAULT_RADIUS_KM = 50;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface SearchSuggestion {
  type: 'event' | 'location' | 'entity';
  data: VectorSearchResult | LocationSuggestion | EntitySuggestion;
}

interface HeaderProps {
  onMenuToggle?: () => void;
  isMenuOpen?: boolean;
}

const HeaderContainer = styled.header`
  background: ${colors.white};
  border-bottom: 1px solid ${colors.border.light};
  box-shadow: ${shadows.sm};
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
`;

const HeaderContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${spacing[4]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: 0 ${spacing[6]};
  }
  
  @media (min-width: ${breakpoints.lg}) {
    padding: 0 ${spacing[8]};
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  gap: ${spacing[4]};
  
  @media (max-width: ${breakpoints.md}) {
    height: 64px;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    flex-wrap: wrap;
    height: auto;
    min-height: 64px;
    padding: ${spacing[2]} 0;
    gap: ${spacing[3]};
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.primary[600]};
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    color: ${colors.primary[700]};
  }
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.xl};
  }
  
  @media (max-width: ${breakpoints.sm}) {
    order: 1;
    flex-shrink: 0;
  }
`;

const LogoCopy = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
`;

const LogoName = styled.span`
  color: inherit;
  white-space: nowrap;
`;

const LogoIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  
  @media (max-width: ${breakpoints.md}) {
    width: 32px;
    height: 32px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 600px;
  margin: 0 ${spacing[4]};
  
  @media (max-width: ${breakpoints.md}) {
    margin: 0 ${spacing[2]};
    max-width: none;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    margin: 0;
    order: 3;
    width: 100%;
    flex-basis: 100%;
  }
`;

const SearchInput = styled.input.withConfig({
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<{ isLoading?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 ${spacing[16]} 0 ${spacing[12]};
  border: 2px solid ${props => props.isLoading ? colors.primary[300] : colors.border.normal};
  border-radius: ${borderRadius.xl};
  font-size: ${typography.fontSizes.base};
  background: ${props => props.isLoading ? colors.primary[50] : colors.background.primary};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${colors.primary[100]};
  }
  
  &::placeholder {
    color: ${colors.text.tertiary};
  }
  
  @media (max-width: ${breakpoints.md}) {
    height: 44px;
    padding: 0 ${spacing[12]} 0 ${spacing[10]};
    font-size: ${typography.fontSizes.sm};
  }
  
  @media (max-width: ${breakpoints.sm}) {
    height: 52px;
    padding: 0 ${spacing[16]} 0 ${spacing[12]};
    font-size: ${typography.fontSizes.base};
    border-radius: ${borderRadius.lg};
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.tertiary};
  width: 20px;
  height: 20px;
  
  @media (max-width: ${breakpoints.md}) {
    left: ${spacing[3]};
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    left: ${spacing[4]};
    width: 20px;
    height: 20px;
  }
`;

const SearchLoadingIcon = styled(Loader2)`
  position: absolute;
  left: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.primary[500]};
  width: 20px;
  height: 20px;
  animation: ${spin} 1s linear infinite;
  
  @media (max-width: ${breakpoints.md}) {
    left: ${spacing[3]};
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    left: ${spacing[4]};
    width: 20px;
    height: 20px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: ${spacing[12]};
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.tertiary};
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${colors.text.secondary};
  }
  
  @media (max-width: ${breakpoints.md}) {
    right: ${spacing[10]};
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    right: ${spacing[12]};
    width: 20px;
    height: 20px;
    -webkit-tap-highlight-color: transparent;
  }
`;

const GoButton = styled.button`
  position: absolute;
  right: ${spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.md};
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.primary[600]};
    transform: translateY(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  @media (max-width: ${breakpoints.md}) {
    right: ${spacing[3]};
    width: 28px;
    height: 28px;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    right: ${spacing[4]};
    width: 32px;
    height: 32px;
    -webkit-tap-highlight-color: transparent;
  }
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.lg};
  max-height: 400px;
  overflow-y: auto;
  z-index: 1001;
  margin-top: ${spacing[1]};
`;

const SuggestionItem = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
  cursor: pointer;
  transition: background-color 0.15s ease;
  
  &:hover {
    background-color: ${colors.background.secondary};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.highlighted {
    background-color: ${colors.primary[50]};
  }
`;

const SuggestionTitle = styled.div`
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[1]};
  font-size: ${typography.fontSizes.sm};
`;

const SuggestionMeta = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const EntitySuggestionContent = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: ${spacing[3]};
`;

const SuggestionText = styled.div`
  min-width: 0;
`;

const SectionSeparator = styled.div`
  padding: ${spacing[2]} ${spacing[4]};
  background-color: ${colors.background.secondary};
  border-bottom: 1px solid ${colors.border.light};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LoadingSpinner = styled.div`
  padding: ${spacing[4]};
  text-align: center;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  
  svg {
    animation: ${spin} 1s linear infinite;
  }
`;

const NavigationIcons = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  
  @media (max-width: ${breakpoints.md}) {
    gap: ${spacing[2]};
  }
  
  @media (max-width: ${breakpoints.sm}) {
    order: 2;
    gap: ${spacing[2]};
  }
`;

const NavIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background-color: ${colors.background.secondary};
    color: ${colors.primary[600]};
  }
  
  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
  
  @media (max-width: ${breakpoints.md}) {
    width: 36px;
    height: 36px;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    width: 44px;
    height: 44px;
  }
`;

const CompactHiddenNavIcon = styled(NavIcon)`
  @media (max-width: ${breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background-color: ${colors.background.secondary};
    color: ${colors.primary[600]};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: ${breakpoints.md}) {
    display: flex;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    width: 44px;
    height: 44px;
  }
`;

export const Header: React.FC<HeaderProps> = ({ onMenuToggle, isMenuOpen }) => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { trackEvent, trackSearch } = useAnalytics();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Cancels the previous typeahead round so a slow earlier response can never
  // repaint results for a query the user has already moved past.
  const inFlightRef = useRef<AbortController | null>(null);
  const [hasError, setHasError] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(async (query: string) => {
    inFlightRef.current?.abort();

    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setHasError(false);
      return;
    }

    const controller = new AbortController();
    inFlightRef.current = controller;
    const isStale = () => controller.signal.aborted || inFlightRef.current !== controller;

    setIsLoading(true);
    setHasError(false);
    setSuggestions([]); // Clear previous suggestions
    setShowSuggestions(true);

    // Each source is settled independently so one failure cannot blank the
    // other two — but a failure also cannot leave that group's stale rows up,
    // because every group was cleared above.
    const [locationResult, entityResult, eventResult] = await Promise.allSettled([
      EventFinderAPI.locationSuggest(query, config.search.locationLimit, controller.signal),
      EventFinderAPI.entitySuggest(query, config.search.entityLimit, undefined, true, controller.signal),
      EventFinderAPI.searchTypeahead(query, config.search.eventLimit),
    ]);

    if (isStale()) return;

    const locationSuggestions: SearchSuggestion[] =
      locationResult.status === 'fulfilled'
        ? locationResult.value.map((location) => ({ type: 'location' as const, data: location }))
        : [];

    const entitySuggestions: SearchSuggestion[] =
      entityResult.status === 'fulfilled'
        ? entityResult.value
            // Defense in depth: City entities duplicate the curated location
            // suggestions and must never appear in the entity group.
            .filter((entity) => (entity.entityType || '').toLowerCase() !== 'city')
            .map((entity) => ({ type: 'entity' as const, data: entity }))
        : [];

    const eventSuggestions: SearchSuggestion[] =
      eventResult.status === 'fulfilled'
        ? (eventResult.value.results || []).map((event) => ({ type: 'event' as const, data: event }))
        : [];

    const allSuggestions = [...locationSuggestions, ...entitySuggestions, ...eventSuggestions];
    const allFailed =
      locationResult.status === 'rejected' &&
      entityResult.status === 'rejected' &&
      eventResult.status === 'rejected';

    if (allFailed) {
      console.error('Search error:', locationResult.reason);
    }

    setSuggestions(allSuggestions);
    setHasError(allFailed);
    setHighlightedIndex(-1);
    setIsLoading(false);

    if (allSuggestions.length > 0) {
      trackSearch(query, allSuggestions.length, {
        event_results: eventSuggestions.length,
        location_results: locationSuggestions.length,
        entity_results: entitySuggestions.length,
      });
    }
  }, [trackSearch]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debounced search
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 300);
  };

  // Handle search clear
  const handleClearSearch = () => {
    inFlightRef.current?.abort();
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setHasError(false);
    searchInputRef.current?.focus();
  };

  // Handle search submission (Enter key or Go button)
  const handleSearchSubmit = () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < 2) return;

    // Close suggestions
    setShowSuggestions(false);
    setHighlightedIndex(-1);

    // Track the search
    trackSearch(trimmedQuery, 0, { search_type: 'direct_search' });

    // Navigate to browse page with search query
    const searchParams = new URLSearchParams();
    searchParams.set('search', trimmedQuery);
    navigate(`/browse?${searchParams.toString()}`);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      // If no suggestions are showing or no suggestions available, handle Enter for search
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearchSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          handleSearchSubmit();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    // Drop the previous page's results the moment a new selection starts so
    // stale events never sit under a newly chosen location or entity.
    setSuggestions([]);
    inFlightRef.current?.abort();
    trackEvent({
      category: 'search',
      action: 'suggestion_select',
      custom_parameters: { action_location: 'header_search', source: suggestion.type },
    });

    if (suggestion.type === 'event') {
      const eventData = suggestion.data as VectorSearchResult;
      setSearchQuery(eventData.title);
      const path = buildEventPath({ eventId: eventData.databaseEventId, title: eventData.title });
      if (path) navigate(path);
    } else if (suggestion.type === 'location') {
      const locationData = suggestion.data as LocationSuggestion;
      setSearchQuery(locationData.displayName);
      // Locations get a canonical, bookmarkable route; the page resolves the
      // slug back to the curated coordinates so the search runs from a point,
      // never from a city-name match.
      if (locationData.canonicalUrl) {
        navigate(locationData.canonicalUrl);
        return;
      }
      const searchParams = new URLSearchParams();
      searchParams.set('city', locationData.city);
      if (locationData.state) searchParams.set('state', locationData.state);
      if (locationData.country) searchParams.set('country', locationData.country);
      searchParams.set('geoCity', '1');
      searchParams.set('radiusKm', String(DEFAULT_RADIUS_KM));
      navigate(`/browse?${searchParams.toString()}`);
    } else if (suggestion.type === 'entity') {
      const entityData = suggestion.data as EntitySuggestion;
      setSearchQuery(entityData.displayName);
      // Entity results are resolved by id + canonical slug, never by fuzzy
      // name matching against event text.
      const path =
        entityData.canonicalUrl ||
        buildEntityEventsPath({
          entityId: entityData.entityId,
          entityName: entityData.entityName,
          searchFriendlyName: entityData.searchFriendlyName,
          entityType: entityData.entityType,
          slug: entityData.slug,
          entityTypeSlug: entityData.entityTypeSlug,
        });
      navigate(path || `/browse?entity=${encodeURIComponent(entityData.entityName)}`);
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      inFlightRef.current?.abort();
    };
  }, []);

  return (
    <HeaderContainer>
      <HeaderContent>
        <HeaderTop>
          <Logo onClick={() => {
            trackEvent({ category: 'navigation', action: 'home_click', custom_parameters: { action_location: 'header_logo', destination: '/' } });
            navigate('/');
          }}>
            <LogoIcon src="/ef_header_logo_small.png" alt="Event Finder Logo" />
            <LogoCopy>
              <LogoName>Event Finder</LogoName>
              <HeaderActivityStatus />
            </LogoCopy>
          </Logo>

          <SearchContainer
            role="combobox"
            aria-expanded={showSuggestions}
            aria-haspopup="listbox"
            aria-owns="header-search-listbox"
          >
            {isLoading ? <SearchLoadingIcon /> : <SearchIcon />}
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder={t('header.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              isLoading={isLoading}
              role="searchbox"
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="header-search-listbox"
              aria-activedescendant={
                highlightedIndex >= 0 ? `header-search-option-${highlightedIndex}` : undefined
              }
              onFocus={() => {
                if (suggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
            />
            {searchQuery && (
              <ClearButton onClick={handleClearSearch} aria-label={t('header.searchButton')}>
                <X />
              </ClearButton>
            )}
            <GoButton onClick={handleSearchSubmit} title={t('header.searchButton')}>
              <ArrowRight size={16} />
            </GoButton>

            {showSuggestions && (
              <SuggestionsDropdown ref={suggestionsRef} id="header-search-listbox" role="listbox">
                {isLoading && (
                  <LoadingSpinner role="status">
                    <Loader2 size={16} />
                    {t('header.searchingEvents')}
                  </LoadingSpinner>
                )}

                {!isLoading && hasError && (
                  <LoadingSpinner role="alert">
                    Search is unavailable right now. Please try again.
                  </LoadingSpinner>
                )}

                {!isLoading && !hasError && suggestions.length === 0 && searchQuery.length >= 2 && (
                  <LoadingSpinner role="status">
                    {t('header.noSuggestionsFound')}
                  </LoadingSpinner>
                )}
                
                {!isLoading && !hasError && (() => {
                  const locationSuggestions = suggestions.filter(s => s.type === 'location');
                  const entitySuggestions = suggestions.filter(s => s.type === 'entity');
                  const eventSuggestions = suggestions.filter(s => s.type === 'event');
                  let currentIndex = 0;
                  
                  return (
                    <>
                      {/* Location Suggestions */}
                      {locationSuggestions.length > 0 && (
                        <>
                          <SectionSeparator>{t('suggestions.locations')}</SectionSeparator>
                          {locationSuggestions.map((suggestion) => {
                            const locationData = suggestion.data as LocationSuggestion;
                            const itemIndex = currentIndex++;
                            
                            return (
                              <SuggestionItem
                                key={`location-${locationData?.city}-${locationData?.state}`}
                                id={`header-search-option-${itemIndex}`}
                                role="option"
                                aria-selected={itemIndex === highlightedIndex}
                                className={itemIndex === highlightedIndex ? 'highlighted' : ''}
                                onClick={() => handleSuggestionClick(suggestion)}
                              >
                                <SuggestionTitle>
                                  {/* Full "City, State, Country" — the short form
                                      cannot disambiguate duplicate city names. */}
                                  {locationData?.fullDisplayName || locationData?.displayName}
                                </SuggestionTitle>
                                <SuggestionMeta>
                                  <MapPin size={14} />
                                  <span>{t('suggestions.location')}</span>
                                  {typeof locationData?.upcomingEventCount === 'number' && (
                                    <>
                                      <span>•</span>
                                      <span>{`${locationData.upcomingEventCount} upcoming`}</span>
                                    </>
                                  )}
                                </SuggestionMeta>
                              </SuggestionItem>
                            );
                          })}
                        </>
                      )}

                      {/* Entity Suggestions */}
                      {entitySuggestions.length > 0 && (
                        <>
                          {(locationSuggestions.length > 0) && <SectionSeparator>{t('suggestions.entities')}</SectionSeparator>}
                          {entitySuggestions.map((suggestion) => {
                            const entityData = suggestion.data as EntitySuggestion;
                            const itemIndex = currentIndex++;
                            
                            return (
                              <SuggestionItem
                                key={`entity-${entityData?.entityId}`}
                                id={`header-search-option-${itemIndex}`}
                                role="option"
                                aria-selected={itemIndex === highlightedIndex}
                                className={itemIndex === highlightedIndex ? 'highlighted' : ''}
                                onClick={() => handleSuggestionClick(suggestion)}
                              >
                                <EntitySuggestionContent>
                                  <EntityAvatar
                                    name={entityData?.displayName || entityData?.entityName || 'Entity'}
                                    entityType={entityData?.entityType}
                                    logoUrl={entityData?.logoUrl}
                                    size={36}
                                  />
                                  <SuggestionText>
                                    <SuggestionTitle>
                                      {entityData?.displayName}
                                    </SuggestionTitle>
                                    <SuggestionMeta>
                                      <Users size={14} />
                                      <span>{displayEntityType(entityData?.entityType)}</span>
                                      {typeof entityData?.upcomingEventCount === 'number' && (
                                        <>
                                          <span>•</span>
                                          <span>{`${entityData.upcomingEventCount} upcoming events`}</span>
                                        </>
                                      )}
                                    </SuggestionMeta>
                                  </SuggestionText>
                                </EntitySuggestionContent>
                              </SuggestionItem>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Event Suggestions */}
                      {eventSuggestions.length > 0 && (
                        <>
                          {(locationSuggestions.length > 0 || entitySuggestions.length > 0) && <SectionSeparator>{t('suggestions.events')}</SectionSeparator>}
                          {eventSuggestions.map((suggestion) => {
                            const eventData = suggestion.data as VectorSearchResult;
                            const itemIndex = currentIndex++;
                            
                            return (
                              <SuggestionItem
                                key={eventData?.id}
                                id={`header-search-option-${itemIndex}`}
                                role="option"
                                aria-selected={itemIndex === highlightedIndex}
                                className={itemIndex === highlightedIndex ? 'highlighted' : ''}
                                onClick={() => handleSuggestionClick(suggestion)}
                              >
                                <SuggestionTitle>
                                  {eventData?.title}
                                </SuggestionTitle>
                                <SuggestionMeta>
                                  <span>Relevance: {Math.round((eventData?.relevanceScore || 0) * 100)}%</span>
                                  {eventData?.eventDetails && eventData.eventDetails[0] && (
                                    <>
                                      <span>•</span>
                                      <span>{eventData.eventDetails[0].entityName}</span>
                                    </>
                                  )}
                                </SuggestionMeta>
                              </SuggestionItem>
                            );
                          })}
                        </>
                      )}
                    </>
                  );
                })()}
              </SuggestionsDropdown>
            )}
          </SearchContainer>

          <NavigationIcons>
            <NavIcon
              type="button"
              title={t('header.createGroup')}
              aria-label={t('header.createGroup')}
              onClick={() => {
                trackEvent({ category: 'navigation', action: 'create_group_click', custom_parameters: { action_location: 'header', destination: '/groups/new' } });
                navigate('/groups/new');
              }}
            >
              <ListPlus aria-hidden="true" size={20} />
            </NavIcon>
            <CompactHiddenNavIcon title={t('header.latestUpdates')} onClick={() => {
              trackEvent({ category: 'navigation', action: 'latest_updates_click', custom_parameters: { action_location: 'header', destination: '/latest-updates' } });
              navigate('/latest-updates');
            }}>
              <Newspaper size={20} />
            </CompactHiddenNavIcon>
            <CompactHiddenNavIcon aria-label={t('header.myEvents')} title={t('header.myEvents')} onClick={() => {
              trackEvent({ category: 'navigation', action: 'my_events_click', custom_parameters: { action_location: 'header', destination: '/my-events' } });
              navigate('/my-events');
            }}>
              <Bookmark aria-hidden="true" size={20} />
            </CompactHiddenNavIcon>
            <CompactHiddenNavIcon aria-label={t('header.mapView')} title={t('header.mapView')} onClick={() => {
              trackEvent({ category: 'navigation', action: 'map_click', custom_parameters: { action_location: 'header', destination: '/map' } });
              navigate('/map');
            }}>
              <MapPin aria-hidden="true" size={20} />
            </CompactHiddenNavIcon>
            <NavIcon 
              title={t('header.feedback')} 
              onClick={() => {
                trackEvent({ category: 'navigation', action: 'feedback_click', custom_parameters: { action_location: 'header', destination: 'feedback_form' } });
                window.open(config.app.feedbackFormUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <MessageSquare size={20} />
            </NavIcon>
            <LocaleSwitcher compact showFullName={false} />
            <MobileMenuButton onClick={onMenuToggle} title={t('header.menu')}>
              <Menu size={20} />
            </MobileMenuButton>
          </NavigationIcons>
        </HeaderTop>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
